import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { campaignsApi, foldersApi, leadsApi, emailAccountsApi } from '../api';
import { toast } from 'sonner';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import Link from '@tiptap/extension-link';
import TextAlign from '@tiptap/extension-text-align';
import { TextStyle } from '@tiptap/extension-text-style';
import { Color } from '@tiptap/extension-color';
import Image from '@tiptap/extension-image';
import {
  ArrowLeft, ArrowRight, Users, Mail, ListOrdered, Clock, Rocket, Loader2,
  CheckCircle, AlertTriangle, Plus, Trash2, Zap, Save, Folder,
  Bold, Italic, UnderlineIcon, Strikethrough, List, ListOrdered as ListOrd,
  AlignLeft, AlignCenter, AlignRight, Link as LinkIcon, ImageIcon, Type, Undo2, Redo2
} from 'lucide-react';

const STEPS = ['Select Leads', 'Email Accounts', 'Sequence', 'Schedule', 'Review & Launch'];
const MERGE_TAGS = ['{CompanyName}', '{Directors}'];
const DAYS_OPTIONS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

function EditorToolbar({ editor }) {
  if (!editor) return null;
  const btn = (active, onClick, children) => (
    <button type="button" onClick={onClick}
      className={`p-1.5 rounded transition-colors ${active ? 'bg-[#D3E3FD] text-[#1A73E8]' : 'text-[#5F6368] hover:bg-[#F1F3F4]'}`}>
      {children}
    </button>
  );
  return (
    <div className="flex flex-wrap items-center gap-0.5 px-2 py-1.5 border-b border-[#E2E8F0] bg-[#F8F9FA] rounded-t-lg">
      {btn(editor.isActive('heading', { level: 2 }), () => editor.chain().focus().toggleHeading({ level: 2 }).run(), <Type className="w-4 h-4" />)}
      <div className="w-px h-5 bg-[#E2E8F0] mx-0.5" />
      {btn(editor.isActive('bold'), () => editor.chain().focus().toggleBold().run(), <Bold className="w-4 h-4" />)}
      {btn(editor.isActive('italic'), () => editor.chain().focus().toggleItalic().run(), <Italic className="w-4 h-4" />)}
      {btn(editor.isActive('underline'), () => editor.chain().focus().toggleUnderline().run(), <UnderlineIcon className="w-4 h-4" />)}
      {btn(editor.isActive('strike'), () => editor.chain().focus().toggleStrike().run(), <Strikethrough className="w-4 h-4" />)}
      <div className="w-px h-5 bg-[#E2E8F0] mx-0.5" />
      {btn(editor.isActive('bulletList'), () => editor.chain().focus().toggleBulletList().run(), <List className="w-4 h-4" />)}
      {btn(editor.isActive('orderedList'), () => editor.chain().focus().toggleOrderedList().run(), <ListOrd className="w-4 h-4" />)}
      <div className="w-px h-5 bg-[#E2E8F0] mx-0.5" />
      {btn(editor.isActive({ textAlign: 'left' }), () => editor.chain().focus().setTextAlign('left').run(), <AlignLeft className="w-4 h-4" />)}
      {btn(editor.isActive({ textAlign: 'center' }), () => editor.chain().focus().setTextAlign('center').run(), <AlignCenter className="w-4 h-4" />)}
      {btn(editor.isActive({ textAlign: 'right' }), () => editor.chain().focus().setTextAlign('right').run(), <AlignRight className="w-4 h-4" />)}
      <div className="w-px h-5 bg-[#E2E8F0] mx-0.5" />
      {btn(editor.isActive('link'), () => {
        if (editor.isActive('link')) { editor.chain().focus().unsetLink().run(); return; }
        const url = window.prompt('URL:');
        if (url) editor.chain().focus().setLink({ href: url }).run();
      }, <LinkIcon className="w-4 h-4" />)}
      {btn(false, () => {
        const url = window.prompt('Image URL:');
        if (url) editor.chain().focus().setImage({ src: url }).run();
      }, <ImageIcon className="w-4 h-4" />)}
      <div className="w-px h-5 bg-[#E2E8F0] mx-0.5" />
      <input type="color" onChange={(e) => editor.chain().focus().setColor(e.target.value).run()}
        className="w-6 h-6 p-0 border-0 rounded cursor-pointer" title="Text color" />
      <div className="w-px h-5 bg-[#E2E8F0] mx-0.5" />
      {btn(false, () => editor.chain().focus().undo().run(), <Undo2 className="w-4 h-4" />)}
      {btn(false, () => editor.chain().focus().redo().run(), <Redo2 className="w-4 h-4" />)}
    </div>
  );
}

function SequenceEditor({ value, onChange, idx }) {
  const editor = useEditor({
    extensions: [
      StarterKit,
      Underline,
      Link.configure({ openOnClick: false }),
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
      TextStyle,
      Color,
      Image,
    ],
    content: value || '',
    onUpdate: ({ editor }) => onChange(editor.getHTML()),
  });

  return (
    <div className="bg-white rounded-lg border border-[#E2E8F0] overflow-hidden" data-testid={`step-${idx}-body`}>
      <EditorToolbar editor={editor} />
      <EditorContent editor={editor} className="prose prose-sm max-w-none px-3 py-2 min-h-[180px] focus-within:ring-2 focus-within:ring-[#1A73E8]/20 text-[#202124]" />
    </div>
  );
}

export default function CampaignCreate() {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);

  // Step 1: Folder selection
  const [folders, setFolders] = useState([]);
  const [selectedFolder, setSelectedFolder] = useState(null);
  const [folderLeadCount, setFolderLeadCount] = useState(0);
  const [leadStatusFilter, setLeadStatusFilter] = useState('all'); // 'all' | 'verified'

  // Step 2: Email accounts
  const [emailAccounts, setEmailAccounts] = useState([]);
  const [accountMode, setAccountMode] = useState('auto'); // 'auto' | 'select'
  const [selectedAccountIds, setSelectedAccountIds] = useState([]);
  const [smtpTesting, setSmtpTesting] = useState(null);
  const [smtpResults, setSmtpResults] = useState({});

  // Step 3: Sequence
  const [sequence, setSequence] = useState([{ step_number: 1, subject: '', body: '', wait_days: 0 }]);

  // Step 4: Schedule
  const [campaignName, setCampaignName] = useState('');
  const [sendStartHour, setSendStartHour] = useState(9);
  const [sendEndHour, setSendEndHour] = useState(18);
  const [sendDays, setSendDays] = useState(['Mon', 'Tue', 'Wed', 'Thu', 'Fri']);
  const [delayMinutes, setDelayMinutes] = useState(5);
  const [dailyLimit, setDailyLimit] = useState(100);
  const [stopOnReply, setStopOnReply] = useState(true);
  const [unsubLink, setUnsubLink] = useState(true);

  useEffect(() => {
    foldersApi.getAll().then(setFolders);
    emailAccountsApi.getAll().then(setEmailAccounts);
  }, []);

  useEffect(() => {
    if (selectedFolder) {
      const params = { folder_id: selectedFolder, limit: 1 };
      if (leadStatusFilter === 'verified') params.status = 'verified';
      leadsApi.getAll(params).then(d => setFolderLeadCount(d.total));
    }
  }, [selectedFolder, leadStatusFilter]);

  const toggleAccount = (id) => {
    setSelectedAccountIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const handleTestSmtp = async (id) => {
    setSmtpTesting(id);
    try {
      const res = await emailAccountsApi.test(id);
      setSmtpResults(prev => ({ ...prev, [id]: res.success }));
      toast[res.success ? 'success' : 'error'](res.message);
    } catch { setSmtpResults(prev => ({ ...prev, [id]: false })); }
    finally { setSmtpTesting(null); }
  };

  const addStep = () => {
    if (sequence.length >= 5) return;
    setSequence([...sequence, { step_number: sequence.length + 1, subject: '', body: '', wait_days: 3 }]);
  };
  const removeStep = (idx) => {
    if (sequence.length <= 1) return;
    setSequence(sequence.filter((_, i) => i !== idx).map((s, i) => ({ ...s, step_number: i + 1 })));
  };
  const updateStep = (idx, field, value) => {
    setSequence(sequence.map((s, i) => i === idx ? { ...s, [field]: value } : s));
  };
  const insertTag = (idx, tag) => updateStep(idx, 'body', sequence[idx].body + tag);

  const handleLaunch = async (draft = false) => {
    if (saving) return;
    if (!campaignName.trim()) { toast.error('Campaign name required'); return; }
    if (!selectedFolder) { toast.error('Select a lead folder'); return; }
    if (!sequence[0].subject || !sequence[0].body) { toast.error('First email step needs subject and body'); return; }

    const emailAccountIds = accountMode === 'auto' ? [] : selectedAccountIds;

    setSaving(true);
    try {
      const res = await campaignsApi.create({
        name: campaignName,
        folder_id: selectedFolder,
        lead_status_filter: leadStatusFilter,
        email_account_ids: emailAccountIds,
        email_account_id: accountMode === 'auto' ? 'auto' : (emailAccountIds[0] || 'auto'),
        sequence,
        send_start_hour: sendStartHour,
        send_end_hour: sendEndHour,
        send_days: sendDays,
        delay_minutes: delayMinutes,
        daily_limit: dailyLimit,
        stop_on_reply: stopOnReply,
        unsubscribe_link: unsubLink,
      });
      if (!draft) {
        await campaignsApi.launch(res.campaign_id);
        toast.success('Campaign launched!');
      } else {
        toast.success('Campaign saved as draft');
      }
      navigate('/outreach/campaigns');
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setSaving(false); }
  };

  const activeAccounts = emailAccounts.filter(a => a.status === 'active');
  const allAccounts = emailAccounts;

  return (
    <div data-testid="campaign-create">
      <button onClick={() => navigate('/outreach/campaigns')} className="flex items-center gap-1 text-sm text-[#5F6368] hover:text-[#202124] mb-4"><ArrowLeft className="w-4 h-4" /> Back</button>

      {/* Steps */}
      <div className="flex items-center gap-2 mb-6">
        {STEPS.map((s, i) => (
          <React.Fragment key={i}>
            <button onClick={() => setStep(i)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                i === step ? 'bg-[#1A73E8] text-white' : i < step ? 'bg-[#E6F4EA] text-[#1E8E3E]' : 'bg-[#F1F3F4] text-[#9AA0A6]'
              }`}>
              {i < step ? <CheckCircle className="w-3.5 h-3.5" /> : <span className="w-4 h-4 rounded-full border border-current flex items-center justify-center text-[10px]">{i + 1}</span>}
              <span className="hidden sm:inline">{s}</span>
            </button>
            {i < STEPS.length - 1 && <div className={`h-px flex-1 ${i < step ? 'bg-[#1E8E3E]' : 'bg-[#E2E8F0]'}`} />}
          </React.Fragment>
        ))}
      </div>

      <div className="bg-white border border-[#E2E8F0] rounded-2xl p-6">
        {/* STEP 1: Select Folder */}
        {step === 0 && (
          <div>
            <h2 className="text-lg font-semibold text-[#202124] mb-4 flex items-center gap-2"><Folder className="w-5 h-5 text-[#1A73E8]" /> Select Lead Folder</h2>
            {folders.length === 0 ? (
              <p className="text-sm text-[#F9AB00] p-4 bg-[#FEF7E0] rounded-xl flex items-center gap-2"><AlertTriangle className="w-4 h-4" /> No folders yet. Create folders in the Leads page first.</p>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {folders.map(f => (
                  <button key={f.id} onClick={() => setSelectedFolder(f.id)}
                    className={`text-left p-4 rounded-xl border transition-colors ${
                      selectedFolder === f.id ? 'border-[#1A73E8] bg-[#E8F0FE]' : 'border-[#E2E8F0] bg-[#F8F9FA] hover:border-[#9AA0A6]'
                    }`}>
                    <div className="flex items-center gap-2 mb-1">
                      <Folder className={`w-5 h-5 ${selectedFolder === f.id ? 'text-[#1A73E8]' : 'text-[#9AA0A6]'}`} />
                      <span className="text-sm font-semibold text-[#202124] truncate">{f.name}</span>
                    </div>
                    <p className="text-xs text-[#5F6368]">{f.lead_count} leads</p>
                  </button>
                ))}
              </div>
            )}
            {selectedFolder && (
              <div className="mt-4 flex items-center gap-3">
                <span className="text-sm text-[#5F6368] font-medium">Include leads:</span>
                <div className="flex gap-2">
                  <button onClick={() => setLeadStatusFilter('all')}
                    className={`px-4 py-1.5 rounded-full text-xs font-semibold transition-colors ${
                      leadStatusFilter === 'all' ? 'bg-[#1A73E8] text-white' : 'bg-[#F1F3F4] text-[#5F6368] hover:bg-[#E8EAED]'
                    }`} data-testid="filter-all-leads">All Leads</button>
                  <button onClick={() => setLeadStatusFilter('verified')}
                    className={`px-4 py-1.5 rounded-full text-xs font-semibold transition-colors ${
                      leadStatusFilter === 'verified' ? 'bg-[#1E8E3E] text-white' : 'bg-[#F1F3F4] text-[#5F6368] hover:bg-[#E8EAED]'
                    }`} data-testid="filter-verified-leads">Verified Only</button>
                </div>
              </div>
            )}
            {selectedFolder && (
              <div className="mt-3 bg-[#E8F0FE] rounded-xl p-4">
                <p className="text-sm text-[#041E49]">
                  <span className="text-2xl font-bold text-[#1A73E8]">{folderLeadCount}</span>
                  {leadStatusFilter === 'verified' ? ' verified' : ''} leads will be included
                </p>
                {leadStatusFilter === 'all' && folderLeadCount > 0 && (
                  <p className="text-xs text-[#5F6368] mt-1">Includes unverified emails — switch to "Verified Only" for better deliverability</p>
                )}
              </div>
            )}
          </div>
        )}

        {/* STEP 2: Email Accounts */}
        {step === 1 && (
          <div>
            <h2 className="text-lg font-semibold text-[#202124] mb-4 flex items-center gap-2"><Mail className="w-5 h-5 text-[#1E8E3E]" /> Email Accounts</h2>

            <div className="space-y-3 mb-4">
              <label onClick={() => { setAccountMode('auto'); setSelectedAccountIds([]); }}
                className={`flex items-center gap-3 p-4 rounded-xl border cursor-pointer transition-colors ${
                  accountMode === 'auto' ? 'border-[#1A73E8] bg-[#E8F0FE]' : 'border-[#E2E8F0] bg-[#F8F9FA] hover:border-[#9AA0A6]'
                }`}>
                <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${accountMode === 'auto' ? 'border-[#1A73E8]' : 'border-[#9AA0A6]'}`}>
                  {accountMode === 'auto' && <div className="w-2 h-2 rounded-full bg-[#1A73E8]" />}
                </div>
                <div>
                  <p className="text-sm font-semibold text-[#202124]">Auto-Rotate All Active Accounts</p>
                  <p className="text-xs text-[#5F6368]">System will distribute sends across all {activeAccounts.length} active accounts based on daily limits</p>
                </div>
              </label>

              <label onClick={() => setAccountMode('select')}
                className={`flex items-center gap-3 p-4 rounded-xl border cursor-pointer transition-colors ${
                  accountMode === 'select' ? 'border-[#1A73E8] bg-[#E8F0FE]' : 'border-[#E2E8F0] bg-[#F8F9FA] hover:border-[#9AA0A6]'
                }`}>
                <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${accountMode === 'select' ? 'border-[#1A73E8]' : 'border-[#9AA0A6]'}`}>
                  {accountMode === 'select' && <div className="w-2 h-2 rounded-full bg-[#1A73E8]" />}
                </div>
                <div>
                  <p className="text-sm font-semibold text-[#202124]">Select Specific Accounts</p>
                  <p className="text-xs text-[#5F6368]">Choose one or more email accounts for this campaign</p>
                </div>
              </label>
            </div>

            {/* Account list - always shown */}
            <div className="space-y-2">
              <p className="text-xs text-[#5F6368] font-semibold uppercase tracking-wide mb-2">
                {accountMode === 'select' ? 'Select accounts for this campaign:' : 'All email accounts:'}
              </p>
              {allAccounts.length === 0 ? (
                <p className="text-sm text-[#F9AB00] p-3 bg-[#FEF7E0] rounded-xl flex items-center gap-2"><AlertTriangle className="w-4 h-4" /> No email accounts configured. Add accounts in Settings.</p>
              ) : allAccounts.map(acc => (
                <div key={acc.id}
                  className={`flex items-center gap-3 p-3 rounded-xl border transition-colors ${
                    accountMode === 'select' && selectedAccountIds.includes(acc.id) ? 'border-[#1A73E8] bg-[#E8F0FE]' : 'border-[#E2E8F0] bg-[#F8F9FA]'
                  } ${acc.status !== 'active' ? 'opacity-50' : ''}`}>
                  {accountMode === 'select' && (
                    <input type="checkbox" checked={selectedAccountIds.includes(acc.id)}
                      onChange={() => toggleAccount(acc.id)}
                      disabled={acc.status !== 'active'}
                      className="rounded border-[#9AA0A6] text-[#1A73E8] focus:ring-[#1A73E8] w-4 h-4" />
                  )}
                  <div className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${acc.status === 'active' ? 'bg-[#1E8E3E]' : 'bg-[#F9AB00]'}`} />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-[#202124] font-medium truncate">{acc.email}</p>
                    <div className="flex items-center gap-3 text-xs text-[#9AA0A6]">
                      <span>{acc.per_minute_limit}/min</span>
                      <span>{acc.per_day_limit}/day</span>
                      <span>Sent: <span className={acc.sent_today >= acc.per_day_limit ? 'text-[#D93025] font-semibold' : 'text-[#1E8E3E] font-semibold'}>{acc.sent_today || 0}</span></span>
                      <span className={`font-semibold ${acc.status === 'active' ? 'text-[#1E8E3E]' : 'text-[#F9AB00]'}`}>{acc.status}</span>
                    </div>
                  </div>
                  <button onClick={() => handleTestSmtp(acc.id)} disabled={smtpTesting === acc.id}
                    className="px-2.5 py-1 text-xs bg-white border border-[#E2E8F0] hover:bg-[#F1F3F4] text-[#5F6368] rounded-full font-medium">
                    {smtpTesting === acc.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <><Zap className="w-3 h-3 inline mr-1" />Test</>}
                  </button>
                  {smtpResults[acc.id] !== undefined && (
                    smtpResults[acc.id] ? <CheckCircle className="w-4 h-4 text-[#1E8E3E]" /> : <AlertTriangle className="w-4 h-4 text-[#D93025]" />
                  )}
                </div>
              ))}
            </div>

            {accountMode === 'select' && selectedAccountIds.length > 0 && (
              <div className="mt-3 bg-[#E8F0FE] rounded-xl p-3">
                <p className="text-sm text-[#041E49]"><span className="font-bold text-[#1A73E8]">{selectedAccountIds.length}</span> account{selectedAccountIds.length > 1 ? 's' : ''} selected — emails will rotate across them</p>
              </div>
            )}
          </div>
        )}

        {/* STEP 3: Sequence */}
        {step === 2 && (
          <div>
            <h2 className="text-lg font-semibold text-[#202124] mb-4 flex items-center gap-2"><ListOrdered className="w-5 h-5 text-[#7C3AED]" /> Email Sequence</h2>
            <div className="space-y-4">
              {sequence.map((s, idx) => (
                <div key={idx} className="bg-[#F8F9FA] rounded-xl p-4 border border-[#E2E8F0]">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-sm font-semibold text-[#202124]">Step {s.step_number} {idx === 0 ? '(Day 0)' : `(Wait ${s.wait_days}d)`}</span>
                    {idx > 0 && <button onClick={() => removeStep(idx)} className="text-[#9AA0A6] hover:text-[#D93025]"><Trash2 className="w-4 h-4" /></button>}
                  </div>
                  {idx > 0 && (
                    <div className="mb-3 flex items-center gap-2">
                      <label className="text-xs text-[#5F6368] font-medium">Wait days:</label>
                      <input type="number" min={1} max={30} value={s.wait_days} onChange={(e) => updateStep(idx, 'wait_days', parseInt(e.target.value) || 1)}
                        className="w-16 bg-white border border-[#E2E8F0] rounded-lg px-2 py-1 text-sm focus:outline-none focus:border-[#1A73E8]" />
                    </div>
                  )}
                  <div className="mb-3">
                    <label className="text-xs text-[#5F6368] font-medium block mb-1">Subject</label>
                    <input value={s.subject} onChange={(e) => updateStep(idx, 'subject', e.target.value)} placeholder="Subject line..."
                      className="w-full bg-white border border-[#E2E8F0] rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#1A73E8] focus:ring-2 focus:ring-[#1A73E8]/20" data-testid={`step-${idx}-subject`} />
                  </div>
                  <div className="mb-2">
                    <label className="text-xs text-[#5F6368] font-medium block mb-1">Body</label>
                    <SequenceEditor value={s.body} onChange={(val) => updateStep(idx, 'body', val)} idx={idx} />
                  </div>
                  <div className="flex flex-wrap gap-1 items-center">
                    <span className="text-xs text-[#9AA0A6] mr-1">Insert:</span>
                    {MERGE_TAGS.map(tag => (
                      <button key={tag} onClick={() => insertTag(idx, tag)}
                        className="px-2 py-0.5 bg-[#E8F0FE] text-[#1A73E8] rounded text-xs hover:bg-[#D3E3FD] font-medium">{tag}</button>
                    ))}
                  </div>
                </div>
              ))}
              {sequence.length < 5 && (
                <button onClick={addStep} className="flex items-center gap-1.5 px-3 py-2 text-sm text-[#1A73E8] hover:bg-[#E8F0FE] border border-dashed border-[#1A73E8]/30 rounded-xl w-full justify-center font-medium">
                  <Plus className="w-4 h-4" /> Add Follow-up Step
                </button>
              )}
            </div>
          </div>
        )}

        {/* STEP 4: Schedule */}
        {step === 3 && (
          <div>
            <h2 className="text-lg font-semibold text-[#202124] mb-4 flex items-center gap-2"><Clock className="w-5 h-5 text-[#F9AB00]" /> Schedule & Settings</h2>
            <div className="space-y-5 max-w-lg">
              <div>
                <label className="text-sm text-[#5F6368] font-medium block mb-1">Campaign Name</label>
                <input value={campaignName} onChange={(e) => setCampaignName(e.target.value)} placeholder="My Campaign"
                  className="w-full bg-[#F1F3F4] border border-transparent rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:bg-white focus:border-[#1A73E8] focus:ring-2 focus:ring-[#1A73E8]/20" data-testid="campaign-name" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm text-[#5F6368] font-medium block mb-1">Start Hour</label>
                  <input type="number" min={0} max={23} value={sendStartHour} onChange={(e) => setSendStartHour(parseInt(e.target.value))}
                    className="w-full bg-[#F1F3F4] border border-transparent rounded-lg px-3 py-2 text-sm focus:outline-none focus:bg-white focus:border-[#1A73E8]" />
                </div>
                <div>
                  <label className="text-sm text-[#5F6368] font-medium block mb-1">End Hour</label>
                  <input type="number" min={0} max={23} value={sendEndHour} onChange={(e) => setSendEndHour(parseInt(e.target.value))}
                    className="w-full bg-[#F1F3F4] border border-transparent rounded-lg px-3 py-2 text-sm focus:outline-none focus:bg-white focus:border-[#1A73E8]" />
                </div>
              </div>
              <div>
                <label className="text-sm text-[#5F6368] font-medium block mb-1.5">Send Days</label>
                <div className="flex flex-wrap gap-2">
                  {DAYS_OPTIONS.map(d => (
                    <button key={d} onClick={() => setSendDays(prev => prev.includes(d) ? prev.filter(x => x !== d) : [...prev, d])}
                      className={`px-3.5 py-1.5 rounded-full text-xs font-semibold transition-colors ${sendDays.includes(d) ? 'bg-[#1A73E8] text-white' : 'bg-[#F1F3F4] text-[#5F6368] hover:bg-[#E8EAED]'}`}>{d}</button>
                  ))}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm text-[#5F6368] font-medium block mb-1">Delay Between Emails</label>
                  <select value={delayMinutes} onChange={(e) => setDelayMinutes(parseInt(e.target.value))}
                    className="w-full bg-[#F1F3F4] border border-transparent rounded-lg px-3 py-2 text-sm focus:outline-none focus:bg-white focus:border-[#1A73E8] cursor-pointer">
                    {[1,2,5,10,15].map(d => <option key={d} value={d}>{d} min</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-sm text-[#5F6368] font-medium block mb-1">Daily Limit</label>
                  <input type="number" min={1} max={500} value={dailyLimit} onChange={(e) => setDailyLimit(parseInt(e.target.value))}
                    className="w-full bg-[#F1F3F4] border border-transparent rounded-lg px-3 py-2 text-sm focus:outline-none focus:bg-white focus:border-[#1A73E8]" />
                </div>
              </div>
              <div className="flex items-center gap-6">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={stopOnReply} onChange={(e) => setStopOnReply(e.target.checked)} className="rounded border-[#9AA0A6] text-[#1A73E8] focus:ring-[#1A73E8]" />
                  <span className="text-sm text-[#202124]">Stop on reply</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={unsubLink} onChange={(e) => setUnsubLink(e.target.checked)} className="rounded border-[#9AA0A6] text-[#1A73E8] focus:ring-[#1A73E8]" />
                  <span className="text-sm text-[#202124]">Add unsubscribe link</span>
                </label>
              </div>
            </div>
          </div>
        )}

        {/* STEP 5: Review */}
        {step === 4 && (
          <div>
            <h2 className="text-lg font-semibold text-[#202124] mb-4 flex items-center gap-2"><Rocket className="w-5 h-5 text-[#1E8E3E]" /> Review & Launch</h2>
            <div className="grid grid-cols-2 gap-4 text-sm mb-6">
              {[
                ['Campaign', campaignName || '-'],
                ['Folder', folders.find(f => f.id === selectedFolder)?.name || '-'],
                ['Leads', `${folderLeadCount} (${leadStatusFilter === 'verified' ? 'Verified Only' : 'All'})`],
                ['Email Accounts', accountMode === 'auto' ? `Auto-rotate (${activeAccounts.length})` : `${selectedAccountIds.length} selected`],
                ['Steps', sequence.length],
                ['Send Window', `${sendStartHour}:00 – ${sendEndHour}:00`],
                ['Daily Limit', dailyLimit],
                ['Stop on Reply', stopOnReply ? 'Yes' : 'No'],
              ].map(([k, v]) => (
                <div key={k} className="bg-[#F8F9FA] rounded-xl p-3 border border-[#E2E8F0]">
                  <p className="text-xs text-[#9AA0A6] font-medium uppercase tracking-wide">{k}</p>
                  <p className="text-[#202124] font-semibold mt-0.5">{v}</p>
                </div>
              ))}
            </div>
            <div className="flex gap-3">
              <button onClick={() => handleLaunch(false)} disabled={saving}
                className="flex items-center gap-2 px-6 py-2.5 bg-[#1A73E8] hover:bg-[#174EA6] text-white rounded-full text-sm font-semibold disabled:opacity-50 shadow-sm" data-testid="launch-campaign-btn">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Rocket className="w-4 h-4" />} Launch Campaign
              </button>
              <button onClick={() => handleLaunch(true)} disabled={saving}
                className="flex items-center gap-2 px-5 py-2.5 bg-white border border-[#E2E8F0] hover:bg-[#F1F3F4] text-[#202124] rounded-full text-sm font-medium disabled:opacity-50" data-testid="save-draft-btn">
                <Save className="w-4 h-4" /> Save as Draft
              </button>
            </div>
          </div>
        )}

        {/* Nav */}
        <div className="flex justify-between mt-6 pt-4 border-t border-[#F1F3F4]">
          <button onClick={() => setStep(Math.max(0, step - 1))} disabled={step === 0}
            className="flex items-center gap-1 px-4 py-2 text-sm text-[#5F6368] hover:text-[#202124] disabled:opacity-30"><ArrowLeft className="w-4 h-4" /> Back</button>
          {step < 4 && (
            <button onClick={() => setStep(Math.min(4, step + 1))}
              className="flex items-center gap-1 px-5 py-2 bg-[#1A73E8] hover:bg-[#174EA6] text-white rounded-full text-sm font-medium shadow-sm" data-testid="next-step-btn">Next <ArrowRight className="w-4 h-4" /></button>
          )}
        </div>
      </div>
    </div>
  );
}
